import { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import TkStepper from "../../components/atoms/TkStepper";
import TkBackButton from "../../components/molecules/TkBackButton";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import { fakeTableList, schema, stepper } from "./parts";
import "./_style.scss";
import { Box } from "@mui/material";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { Prefectures } from "../../assets/data/Constants";
import TkDateDialog from "../../components/molecules/TkDateDialog";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { IAPI_RET017_Response, IAPI_RET018_Request } from "../../type/apiType";
import { httpAPI_RET017, httpAPI_RET018 } from "./api/request";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import { labelids } from "../../assets/data/TkExplanationProperty";
import { API_RET018_BuildReqParams } from "../../utils/buildRequestParams";
import { changeZipcodeFormat } from "../../utils/useTools";
import _ from "lodash";
import { IJob_40Props } from "../../type/contextType/businessContext/IJob_40";
import { TkGetCode } from "../../utils/TkGetCode";

/**
 * SCRR410022 退職手当金受領者の変更手続き（機構へ連絡）
 * @return SCRR410022
 */
const SCRR410022 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const context = businessState.job_40;
  const userType = state.authInfo?.userType;
  const isUserTypeKiko = userType === authorityUserType.userTypeNumber.KIKO;
  const contractorNo = isUserTypeKiko
    ? businessState.common?.contractorNo
    : state.authInfo?.contractorNo;
  const applyId = location.state?.applyId;
  const [stateTableList, setStateTableList] = useState<any>([]);
  const [isSubmitOpen, setIsSubmitOpen] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const stateFakeTableList = useRef(_.cloneDeep(fakeTableList));

  const handleInit = async () => {
    let data: IJob_40Props | IAPI_RET017_Response = { ...context };

    // 法人ホームの手続履歴から遷移した場合
    if (applyId) {
      // 受領者変更参照APIを呼び出す
      try {
        const res = await httpAPI_RET017({ contractorNo, applyId });

        if (res.resultCd === "I0000") {
          data = res;
        } else {
          setState({
            ...state,
            errorMsgId: res.resultCd,
          });
          return;
        }
      } catch {
        navigate("/error");
      }
    }

    let resArr = stateFakeTableList.current;

    // 退職者名
    if (isUserTypeKiko) {
      let retireeName = resArr.find((item) => item.keyword === "retireeName")!;
      retireeName.showLine = true;
      retireeName.text =
        (data?.staffNo ?? "") + ":" + (data?.retirementStaffName ?? "");
    }

    // 請求者氏名（ｶﾅ）
    let demanderNameKana = resArr.find(
      (item) => item.keyword === "demanderNameKana"
    )!;
    demanderNameKana.text = data?.demanderNameKana;

    // 請求者氏名
    let demanderName = resArr.find((item) => item.keyword === "demanderName")!;
    demanderName.text = data?.demanderName;

    // 続柄
    let demanderRelationship = resArr.find(
      (item) => item.keyword === "demanderRelationship"
    )!;
    demanderRelationship.setDefaultValue = data?.demanderRelationship;

    // その他
    let demanderRelationshipOthers = resArr.find(
      (item) => item.keyword === "demanderRelationshipOthers"
    )!;
    if (data?.demanderRelationship === "8") {
      demanderRelationshipOthers.showLine = true;
      demanderRelationshipOthers.text = data?.demanderRelationshipOthers;
    }

    // 請求者連絡先
    let tel = resArr.find((item) => item.keyword === "tel")!;
    tel.text = data?.tel;

    // メールアドレス
    let demanderEmail = resArr.find(
      (item) => item.keyword === "demanderEmail"
    )!;
    demanderEmail.text = data?.demanderEmail;

    // 請求者現住所（郵便番号）
    let demanderZipcode = resArr.find(
      (item) => item.keyword === "demanderZipcode"
    )!;
    demanderZipcode.text = changeZipcodeFormat(data?.demanderZipcode);

    // 請求者現住所（郵便番号以外）
    let address = resArr.find((item) => item.keyword === "address")!;
    address.disabled = true;
    address.valueObject = {
      prefectures: TkGetCode(Prefectures, data?.demanderPrefectureCd) ?? "",
      city: data?.demanderCity ?? "",
      address: data?.demanderAddressOthers ?? "",
    };

    // 退職者からの生計維持の有無
    let livelihoodFromRetireeFlg = resArr.find(
      (item) => item.keyword === "livelihoodFromRetireeFlg"
    )!;
    livelihoodFromRetireeFlg.setDefaultValue = data?.livelihoodFromRetireeFlg;

    // あなたと同順位の請求権保持者の有無
    let demanderFlg = resArr.find((item) => item.keyword === "demanderFlg")!;
    demanderFlg.setDefaultValue = data?.demanderFlg;

    const file = [
      { key: "deathCertificate", class: "14" },
      { key: "livelihoodSupportRelatives", class: "10" },
      { key: "livelihoodNoSupportRelatives", class: "10" },
      { key: "livelihoodSupportWithoutFormalMarriage", class: "10" },
      { key: "certificateOfResidence", class: "21" },
      { key: "lawfulPartner", class: "22" },
    ];
    if (data?.fileList) {
      file.forEach((file) => {
        data?.fileList?.forEach((fileInfo) => {
          let fileObj = resArr.find(
            (item) =>
              item.keyword === file.key && fileInfo?.formClass === file.class
          )!;
          if (fileObj) {
            fileObj.dataname = fileInfo?.formName;
            fileObj.base64filedata = fileInfo?.file;
          }
        });
      });
    }

    // 「死亡診断書」又は「死体検案書」（写）
    let deathCertificate = resArr.find(
      (item) => item.keyword === "deathCertificate"
    )!;
    // 職員の死亡当時、主として職員の収入により生計を維持していた親族がいることを証する「生計維持に関する調査書」（様式１１号）
    let livelihoodSupportRelatives = resArr.find(
      (item) => item.keyword === "livelihoodSupportRelatives"
    )!;
    // 職員の死亡当時、主として職員の収入により生計を維持していた親族がいないことを証する「生計維持に関する調査書」（様式１１号）
    let livelihoodNoSupportRelatives = resArr.find(
      (item) => item.keyword === "livelihoodNoSupportRelatives"
    )!;
    // 内縁関係についての「生計維持に関する調査書」（様式１１号）
    let livelihoodSupportWithoutFormalMarriage = resArr.find(
      (item) => item.keyword === "livelihoodSupportWithoutFormalMarriage"
    )!;
    // 職員の死亡当時の世帯員の状況を明らかにできる「住民票の謄本」
    let certificateOfResidence = resArr.find(
      (item) => item.keyword === "certificateOfResidence"
    )!;
    // その他、事実上の配偶者であることを証明する書類
    let lawfulPartner = resArr.find(
      (item) => item.keyword === "lawfulPartner"
    )!;
    if (data?.demanderRelationship === "1") {
      deathCertificate.showLine = true;
      livelihoodSupportRelatives.showLine = false;
      livelihoodNoSupportRelatives.showLine = false;
      livelihoodSupportWithoutFormalMarriage.showLine = false;
      certificateOfResidence.showLine = false;
      lawfulPartner.showLine = false;
    } else if (data?.demanderRelationship === "2") {
      deathCertificate.showLine = true;
      livelihoodSupportRelatives.showLine = false;
      livelihoodNoSupportRelatives.showLine = false;
      livelihoodSupportWithoutFormalMarriage.showLine = true;
      certificateOfResidence.showLine = true;
      lawfulPartner.showLine = true;
    } else if (Number(data?.demanderRelationship ?? "") > 2) {
      deathCertificate.showLine = true;
      livelihoodSupportRelatives.showLine =
        data?.livelihoodFromRetireeFlg === "1";
      livelihoodNoSupportRelatives.showLine =
        data?.livelihoodFromRetireeFlg === "0";
      livelihoodSupportWithoutFormalMarriage.showLine = false;
      certificateOfResidence.showLine = false;
      lawfulPartner.showLine = false;
    }

    setStateTableList([...resArr]);
  };

  // 初期表示
  useEffect(() => {
    handleInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const makeHttpObj = (isTempSave?: boolean): IAPI_RET018_Request => {
    const reqObj: IAPI_RET018_Request = {
      ...context,
      tempSaveStep: isTempSave ? 4 : 0,
      contractorNo,
      screenId: "SCRR410022",
    };
    return API_RET018_BuildReqParams(reqObj);
  };

  const onCheckAndSubmit = async (index?: number) => {
    if (index === 0) {
      try {
        const res = await httpAPI_RET018({
          ...makeHttpObj(true),
        });

        if (res.resultCd === "I0000") {
          setState({
            ...state,
            errorMsgId: "",
          });
          setBusinessState({
            job_40: {
              ...context,
              tempSaveStep: 4,
              applyId: res.applyId,
            },
          });
          setOpen(true);
        } else {
          setState({
            ...state,
            errorMsgId: res.resultCd,
          });
        }
      } catch (error) {
        navigate("/error");
      }
    } else {
      setIsSubmitOpen(true);
    }
  };

  // 申請 / 機構へ提出する
  const handleSubmitChange = async (date?: any) => {
    try {
      const res = await httpAPI_RET018({
        ...makeHttpObj(false),
        approvalDate: date,
      });

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        navigate("/SCRC070008");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
        setIsSubmitOpen(false);
      }
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box className="SCRR410022" data-testid="SCRR410022PageForTest">
      <TkStepper
        steps={stepper}
        activeStep={3}
        savaStep={context?.tempSaveStep}
      />
      <TkInfoManual rule={"\\n"}>{labelids.labelid.LBLR410022_01}</TkInfoManual>
      <TkFakeTable
        list={stateTableList}
        ruleSchema={schema}
        onCheckAndSubmit={(_, index) => {
          onCheckAndSubmit(index);
        }}
        buttonGroup={<TkBackButton />}
        submitButtonGroup={[
          { submitButtonText: "一時保存", showButton: !applyId },
          {
            submitButtonText: isUserTypeKiko ? "申請" : "機構へ提出する",
            showButton: !applyId,
          },
        ]}
      />

      {isUserTypeKiko ? (
        <TkDateDialog
          open={isSubmitOpen}
          onOky={handleSubmitChange}
          onCancel={() => setIsSubmitOpen(false)}
          minDate={new Date("1900/01/01")}
          maxDate={new Date()}
          title="確認（申請）"
          contextText="　申請を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      ) : (
        <TkMessageDialog
          open={isSubmitOpen}
          onOky={handleSubmitChange}
          onCancel={() => setIsSubmitOpen(false)}
          title="確認（機構へ提出する）"
          contextText="　機構へ提出を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      )}

      <TkMessageDialog
        open={open}
        onOky={() => setOpen(false)}
        title="一時保存完了"
        contextText="　一時保存が完了しました。"
        okText="はい"
        cancelText=""
      />
    </Box>
  );
};

export default SCRR410022;
